import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BrandLogo from "@/components/BrandLogo";
import {
    Sparkles, ArrowRight, User as UserIcon, Users, Home as HomeIcon,
    ShieldCheck, CreditCard, Repeat, FileText, PieChart,
} from "lucide-react";

/**
 * VAULT · Public Demo Landing
 *
 * Purely static, incognito-safe. Each "Start tour" links to a fully public
 * `/demo/{persona}` page — no backend call, no auth cookie, no chance of
 * bouncing to /login. Uses the Midnight Luxe design tokens directly.
 */
const TOURS = [
    {
        key: "personal", to: "/demo/personal", label: "Personal VAULT",
        icon: UserIcon, tagline: "One person. One organised financial life.",
        highlights: ["3 credit cards", "5 subscriptions", "Assets & liabilities", "Documents & analytics"],
        accent: "from-blue-500/20 to-blue-500/0",
        testid: "demo-personal",
    },
    {
        key: "couple", to: "/demo/couple", label: "Couple VAULT",
        icon: Users, tagline: "Mine. Partner. Joint. Together.",
        highlights: ["Private + shared zones", "Joint bills", "Shared subscriptions", "Together overview"],
        accent: "from-violet-500/20 to-violet-500/0",
        testid: "demo-couple",
    },
    {
        key: "family", to: "/demo/family", label: "Family VAULT",
        icon: HomeIcon, tagline: "Shared responsibilities. Individual privacy.",
        highlights: ["4 family members", "Household calendar", "Shared docs & policies", "Owner per task"],
        accent: "from-emerald-500/20 to-emerald-500/0",
        testid: "demo-family",
    },
    {
        key: "business", to: "/demo/business", label: "Business Owner VAULT",
        icon: HomeIcon, tagline: "One life. Two ledgers. Cleanly separate.",
        highlights: ["Personal + Business zones", "GST filings & vendor invoices", "Cloud / SaaS subs", "Tax + incorporation docs"],
        accent: "from-amber-500/20 to-amber-500/0",
        testid: "demo-business",
    },
];

export default function DemoLanding() {
    return (
        <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="marketing-demo">
            <Helmet>
                <title>Live Demo · See VAULT in Action · VAULT by PD</title>
                <meta name="description" content="Explore VAULT using completely synthetic financial information. Personal, Couple and Family demos — no signup required." />
                <link rel="canonical" href="https://vaultbypd.com/demo" />
            </Helmet>

            {/* Public nav (Midnight Luxe) */}
            <header className="border-b border-white/10 bg-[#0b1e3f] sticky top-0 z-40" data-testid="demo-public-nav">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-6">
                    <Link to="/">
                        <BrandLogo variant="light" height={26} />
                    </Link>
                    <nav className="hidden md:flex items-center gap-4 text-[13px] text-white/70 ml-2">
                        <Link to="/product"  className="hover:text-white">Product</Link>
                        <Link to="/pricing"  className="hover:text-white">Pricing</Link>
                        <Link to="/demo"     className="text-white">Demo</Link>
                        <Link to="/together" className="hover:text-white">Together</Link>
                        <Link to="/trust"    className="hover:text-white">Trust</Link>
                    </nav>
                    <div className="ml-auto flex items-center gap-2">
                        <Link to="/login"  className="text-[13px] font-semibold text-white/70 hover:text-white hidden sm:inline">Sign in</Link>
                        <Link to="/signup" className="text-[12.5px] font-semibold text-[#0b1e3f] bg-amber-300 rounded-full px-3.5 py-1.5 hover:bg-amber-200" data-testid="demo-nav-signup">
                            Create your VAULT
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="bg-gradient-to-br from-[#0b1e3f] via-[#0b1e3f] to-[#152a52] text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <div className="absolute -top-20 -left-10 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl"/>
                    <div className="absolute -bottom-20 -right-10 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl"/>
                </div>
                <div className="max-w-6xl mx-auto px-6 py-16 relative">
                    <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-widest text-amber-300 bg-amber-300/10 rounded-full px-2 py-0.5 border border-amber-300/30 mb-4">
                        <Sparkles size={11}/> Live Demo · Sample data only
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl" data-testid="marketing-hero">
                        See VAULT in action.
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
                        Explore VAULT using completely synthetic financial information.
                        No signup required. Nothing you do here affects a real account.
                    </p>
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link to="/demo/personal" className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#0b1e3f] bg-amber-300 hover:bg-amber-200 rounded-full px-5 py-2.5" data-testid="demo-hero-primary">
                            Explore Personal VAULT <ArrowRight size={13} />
                        </Link>
                        <Link to="#tours" className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white border border-white/30 hover:border-white rounded-full px-5 py-2.5" data-testid="demo-hero-secondary">
                            Choose another demo
                        </Link>
                    </div>
                </div>
            </section>

            {/* Three tour cards */}
            <section id="tours" className="max-w-6xl mx-auto px-6 py-14" data-testid="demo-tours">
                <div className="text-[10.5px] uppercase tracking-widest text-amber-700 font-bold">Three ways to explore</div>
                <h2 className="font-serif text-3xl text-stone-900 mt-1.5">Pick the demo that fits your life.</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                    {TOURS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <Link
                                key={t.key}
                                to={t.to}
                                className="group text-left rounded-2xl border border-stone-200 bg-white overflow-hidden hover:border-[#0b1e3f] hover:shadow-xl transition-all"
                                data-testid={t.testid}
                            >
                                {/* Preview strip mimicking the dashboard */}
                                <div className={`bg-gradient-to-br ${t.accent} p-4 border-b border-stone-100 relative`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-9 h-9 rounded-lg bg-[#0b1e3f] text-amber-300 flex items-center justify-center">
                                            <Icon size={16}/>
                                        </div>
                                        <div className="text-[10.5px] uppercase tracking-widest text-stone-700 font-bold">{t.label}</div>
                                    </div>
                                    <div className="mt-4 grid grid-cols-3 gap-1.5">
                                        {[CreditCard, Repeat, PieChart].map((I, i) => (
                                            <div key={i} className="h-12 rounded-lg bg-white/70 border border-stone-200 flex items-center justify-center text-stone-500">
                                                <I size={14}/>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                                        <div className="h-6 rounded-md bg-white/70 border border-stone-200"/>
                                        <div className="h-6 rounded-md bg-white/70 border border-stone-200"/>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="font-serif text-lg text-stone-900">{t.tagline}</div>
                                    <ul className="mt-3 space-y-1.5">
                                        {t.highlights.map((h) => (
                                            <li key={h} className="text-[12.5px] text-stone-600 flex items-center gap-1.5">
                                                <ShieldCheck size={11} className="text-emerald-600"/> {h}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-4 text-[12.5px] font-semibold text-[#0b1e3f] group-hover:text-amber-700 inline-flex items-center gap-1">
                                        Start tour <ArrowRight size={12}/>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* What you'll experience */}
            <section className="max-w-6xl mx-auto px-6 pb-14">
                <div className="rounded-2xl bg-white border border-stone-200 p-6">
                    <div className="text-[10.5px] uppercase tracking-widest text-amber-700 font-bold">You&rsquo;ll experience</div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { icon: CreditCard, label: "Cards" },
                            { icon: Repeat,     label: "Subscriptions" },
                            { icon: FileText,   label: "Documents" },
                            { icon: PieChart,   label: "Analytics" },
                            { icon: Users,      label: "Household" },
                            { icon: HomeIcon,   label: "Assets" },
                            { icon: ShieldCheck, label: "Privacy" },
                            { icon: Sparkles,   label: "Brief" },
                        ].map(({ icon: I, label }) => (
                            <div key={label} className="rounded-xl border border-stone-200 bg-stone-50 p-3 flex items-center gap-2">
                                <I size={14} className="text-[#0b1e3f]"/>
                                <span className="text-[12.5px] font-medium text-stone-800">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust strip */}
            <section className="max-w-6xl mx-auto px-6 pb-14">
                <div className="border-l-4 border-emerald-300 bg-emerald-50/40 px-4 py-3 text-[12.5px] text-stone-700 flex items-start gap-2 rounded-r-xl">
                    <ShieldCheck size={13} className="mt-0.5 text-emerald-800 shrink-0" />
                    All demo data is synthetic. The demo is read-only, resettable, and no signup is required.
                    Nothing you see here reflects real customer information.
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="demo-bottom-cta">
                <div className="rounded-2xl bg-[#0b1e3f] text-white px-6 py-8 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-300">Ready?</div>
                        <div className="font-serif text-2xl mt-1">Bring your financial life into VAULT.</div>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/signup" className="text-[13px] font-semibold text-[#0b1e3f] bg-amber-300 hover:bg-amber-200 rounded-full px-5 py-2.5" data-testid="demo-bottom-cta-signup">
                            Create your VAULT
                        </Link>
                        <Link to="/pricing" className="text-[13px] font-semibold text-white border border-white/30 hover:border-white rounded-full px-5 py-2.5">
                            See pricing
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-stone-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-6 text-[11px] text-stone-500 flex flex-wrap items-center gap-3">
                    <BrandLogo variant="dark" height={20}/>
                    <span className="ml-2">© {new Date().getFullYear()} VAULT by PD · Financial life, organised.</span>
                    <span className="ml-auto"><Link to="/privacy" className="hover:text-stone-900">Privacy</Link> · <Link to="/terms" className="hover:text-stone-900">Terms</Link> · <Link to="/trust" className="hover:text-stone-900">Trust</Link></span>
                </div>
            </footer>
        </div>
    );
}
